// ============================================================================
// Vendor Dashboard — the TRANSPORTER's view of the work: every booking in scope
// laid out as the trips that carry it. PHASE 1.
//
// One page, no tabs. The board (`../logistics`) splits First Mile / Last Mile
// because two different ops desks work them; a transporter does not care which
// half of our operation a run belongs to — it is their vehicle either way — so
// this page sends no `board_lane` at all and both halves come back in one list.
//
// LAYOUT: flat trip groups, nothing else — ONE PANEL PER NAMED LOGINEXT TRIP,
// grouped by trip name on the server and ordered there too — vendor A→Z, then the
// most stops first, each trip's stops in planned-ETA order. (Not the board's "Group
// by trip (vehicle)" key, which is one panel per truck: over a date range a truck
// runs several trips, and that key folds them all into one heading. Keying on the
// trip name is safe here only because the page asks for `has_trip`.) So a heading
// here is one run, and the rows under it are that run's stops. There are deliberately
// no board SECTIONS (escalations / to-assign / on-a-truck …): sections answer
// "what does the desk owe this booking", which is not this page's question. That
// is also why the ESCALATION column is always mounted here — with no escalation
// section to lift a flagged row into, the column is the only place its reason
// can live.
//
// PHASE 1 SCOPE — read this before trusting the page:
//   - DATA comes from the EXISTING board endpoint (`listLogisticsOrders`, i.e.
//     oms/api/views/logistics.py's LogisticsOrderListView). Every column is real
//     except Live ETA.
//   - NOT YET SCOPED TO ONE TRANSPORTER. The page shows every trip in the date /
//     city / carrier scope, whoever is driving it. The conditions that identify a
//     transporter's own rows are phase 2 (`Carrier` is the nearest stand-in
//     meanwhile) — until then the notice under the count line says so on screen.
//   - LIVE ETA is a mounted but blank column; its source is still to be decided.
//
// Pieces alongside: `controls` (strip + filters), `columns` (the table columns).
// ============================================================================
import { CarOutlined, EnvironmentOutlined, ExportOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Collapse,
  Drawer,
  Empty,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "../index.module.scss";
import { LogisticsOrderRow } from "../types";
import { fmtIST } from "../format";
import BookingDetails, { fromRow } from "../components/booking_details";
import { CrmIssueDrawer } from "../components/crm_issue_thread";
import AllocationItemsModal from "../logistics/components/allocation_items_modal";
import { canHaveLiveEta } from "./live_eta";
import {
  getLiveEtas,
  getLoginextTrackingEmbed,
  getPlannedTrip,
  PlannedTrip,
  listWarehouses,
  WarehouseOption,
} from "../logistics/logistics.api";
import { buildVendorColumns } from "./columns";
import VendorControls from "./controls";
import { trackingEmbedUrl } from "./loginext_embed";
import TripMap from "./trip_map";
import TrackingPanel from "./tracking_panel";
import VendorSummary, { MileSlice, SummaryRequest } from "./summary";
import DriverPerformanceDrawer from "./driver_performance_drawer";
import SummaryListDrawer from "./summary_drawer";
import {
  CarrierSummaryResponse,
  CarrierTrip,
  downloadCarrierTripsCsv,
  getCarrierSummary,
  listCarrierTrips,
} from "./carrier.api";
import { Pagination, Segmented } from "antd";

// The distinct Loginext trip names across a group's rows, order-preserving. Usually exactly one —
// every booking on a trip is stamped by the same `assign_trip_vehicle` call — but a vehicle re-
// allotted mid-day carries two, and a truck whose vehicle was entered by hand rather than through a
// trip carries none (then the heading is just vehicle · driver, as before).
// The transporter running the trip, named outright by LogiNext as `transport_vendor_name` on the
// `trip_vehicle_assigned` payload.
//
// This replaced deriving it from the first word of the driver name ("JMJ HH FM Naveen" → "JMJ").
// That only ever worked while every driver name began with its vendor, and would have failed
// silently — as a wrong vendor, not a blank one — the first time one did not.
export const vendorOf = (row: LogisticsOrderRow): string | null =>
  row.trip_vendor_name?.trim() || null;

// One labelled cell in a collapsed trip's header — a small caption over its value, the same shape
// Delivery V2's trip header uses so the two read alike.
const HeaderField: React.FC<{ label: string; grow?: number; children: React.ReactNode }> = ({
  label,
  grow = 1,
  children,
}) => (
  <span style={{ flexGrow: grow, flexBasis: 0, minWidth: 110 }}>
    <Typography.Text strong style={{ fontSize: 12, display: "block", color: "#8c8c8c" }}>
      {label}
    </Typography.Text>
    {children}
  </span>
);

// The trip's stamped driver, the full name — what the Driver filter matches on. Same field the
// vendor is derived from, so the two filters agree about which name they are talking about.
export const driverOf = (row: LogisticsOrderRow): string | null =>
  row.trip_driver_name?.trim() || null;

// Trips per page. The server caps and defaults to the same number.
const PAGE_SIZE = 20;

const VendorDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  // One page of trips, plus the totals that describe the WHOLE filtered range (never the page —
  // a figure that moved as you paged would be worse than no figure).
  const [trips, setTrips] = useState<CarrierTrip[]>([]);
  const [tripCount, setTripCount] = useState(0);
  const [bookingCount, setBookingCount] = useState(0);
  const [numPages, setNumPages] = useState(1);
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState<CarrierSummaryResponse | undefined>(undefined);

  // Filters. A bounded dispatch WINDOW, not the board's open-ended "on or before". That difference
  // is load-bearing: the board can leave the lower bound off because it drops delivered bookings in
  // SQL, but this page keeps them (`include_delivered` below), and an unbounded window would then
  // join and serialise every delivery the system has ever completed — see `_exclude_delivered`'s
  // docstring in oms/logistics/selectors.py. Defaults to today only.
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([dayjs(), dayjs()]);
  const [city, setCity] = useState<string | undefined>(undefined);
  const [warehouse, setWarehouse] = useState<number | undefined>(undefined);
  // Kept although nothing sets it today: the "All" chip reads it, and it is the seam an
  // attention filter would come back through. The per-bucket chips were removed from the strip.
  const [attention, setAttention] = useState("");
  const [expressOnly, setExpressOnly] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Filtered in the browser, not server-side: both are read off the trip payload the backend stamps
  // onto each row (no column to filter on), and the page loads every row in scope unpaginated, so
  // narrowing here is exact.
  const [vendor, setVendor] = useState<string | undefined>(undefined);
  const [driver, setDriver] = useState<string | undefined>(undefined);
  // Delivered / still out. Derived from the row's delivery status (no column to filter on), so like
  // Vendor and Driver it narrows in the browser over rows already loaded.
  const [deliveryState, setDeliveryState] = useState<"delivered" | "not_delivered" | undefined>(
    undefined
  );
  // First mile / last mile. A trip can be MIXED — an inbound pickup and a customer drop on the same
  // truck — so this narrows the SHIPMENTS, not the trips: a mixed trip keeps its heading and shows
  // only the half you asked for, and a trip that is entirely the other half drops out on its own.
  const [mile, setMile] = useState<MileSlice | undefined>(undefined);
  // Which trip panels the operator has opened. The live-ETA pass follows these — see below.
  const [openTrips, setOpenTrips] = useState<string[]>([]);
  // booking id → LogiNext's live ETA. Filled in AFTER the board renders, by the pass below.
  const [liveEta, setLiveEta] = useState<Record<number, string | null>>({});
  const [liveEtaLoading, setLiveEtaLoading] = useState(false);
  // The lookup itself failed (endpoint unreachable / errored) — distinct from LogiNext having no
  // ETA, and the column must not report one as the other.
  const [liveEtaFailed, setLiveEtaFailed] = useState(false);
  // Bumped whenever a reload clears the ETAs. It is a DEPENDENCY of the fetch pass, which is what
  // makes the pass run again after a clear: without it a reload that leaves the same shipments on
  // screen leaves `openEtaKey` unchanged, the effect never re-fires, and every open panel sits on
  // the planned ETA until the operator collapses and reopens it.
  const [etaGen, setEtaGen] = useState(0);
  const [tab, setTab] = useState("trips");
  const [summaryRequest, setSummaryRequest] = useState<SummaryRequest | null>(null);
  const [driversOpen, setDriversOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);

  // Overlays, each a single nullable row (the house convention).
  const [itemsFor, setItemsFor] = useState<LogisticsOrderRow | null>(null); // contents (row click)
  const [viewing, setViewing] = useState<LogisticsOrderRow | null>(null); // read-only details
  const [notesFor, setNotesFor] = useState<LogisticsOrderRow | null>(null); // read-only thread
  // Who may close an escalation out: the `oms.update` permission — the same slug the server's
  // resolve endpoint requires. Reading this page is a wider right than resolving on it, so the
  // "Mark resolved" link and the menu item are hidden without it, exactly as on the board.
  // What is embedded in the LogiNext drawer. Only their TRACKING page can be framed — it takes the
  // iFrame token in the URL. Their trip page is logged-in product UI and prompts for a sign-in
  // whatever token you pass, so that one opens in a tab instead.
  const [frame, setFrame] = useState<{ title: string; url: string | null; docId: number } | null>(
    null
  );
  // Which tracking view is showing. OURS is the default: it is built from our own record and always
  // renders, where the LogiNext embed is a coin toss on their session (see the drawer).
  const [trackSource, setTrackSource] = useState<"ours" | "loginext">("ours");
  // The trip whose planned route is open on the map, and which one is being fetched.
  const [tripRoute, setTripRoute] = useState<PlannedTrip | null>(null);
  const [loadingRoute, setLoadingRoute] = useState<string | null>(null);
  // Bumped to force the iframe to remount — the way back after signing in to Loginext in another
  // tab, since we cannot read the frame (cross-origin) to notice that the session arrived.
  const [frameNonce, setFrameNonce] = useState(0);

  // Type-to-search debounce.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // What the server scopes on. The row filters (vendor / driver / mile / delivery state) are in
  // here now rather than applied in the browser: they read DERIVED values, so they were never
  // SQL filters, and the server folds them in the same pass that builds the counts — which is
  // what lets a page of 20 trips describe the same set the Summary counts.
  const scope = useMemo(
    () => ({
      dispatch_from: dateRange[0].format("YYYY-MM-DD"),
      dispatch_to: dateRange[1].format("YYYY-MM-DD"),
      city,
      warehouse_id: warehouse,
      express: expressOnly || undefined,
      vendor,
      driver,
      mile,
      delivery_state: deliveryState === "delivered" ? "delivered" : deliveryState ? "not_delivered" : undefined,
    }),
    [dateRange, city, warehouse, expressOnly, vendor, driver, mile, deliveryState]
  );

  const load = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!opts.silent) setLoading(true);
      // TWO requests on purpose. The trip page is bounded (20 trips) and renders straight away;
      // the stats span the whole filtered range, so folding them into the list would make a wide
      // date range block the table behind it.
      const scoped = {
        ...scope,
        // A search must surface the hit whatever its dispatch date, so both bounds come off while
        // one is active (the server drops the rest of its own cuts to match).
        ...(search ? { dispatch_from: undefined, dispatch_to: undefined } : {}),
        search: search || undefined,
      };
      const [list, stats] = await Promise.all([
        listCarrierTrips({ ...scoped, page, page_size: PAGE_SIZE }),
        getCarrierSummary(scoped),
      ]);
      if (list) {
        setTrips(list.results);
        setTripCount(list.count);
        setBookingCount(list.bookings);
        setNumPages(list.num_pages);
        setError(false);
        setUpdatedAt(Date.now());
      } else {
        setError(true);
      }
      if (stats) setStats(stats);
      if (!opts.silent) setLoading(false);
    },
    [scope, search, page]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Any filter change resets to the first page — otherwise a narrower filter can leave you sitting
  // on page 4 of a two-page result, looking at nothing.
  useEffect(() => {
    setPage(1);
  }, [scope, search]);

  // MD warehouses for the filter — fetched once (they change rarely).
  useEffect(() => {
    listWarehouses().then(setWarehouses);
  }, []);

  // No auto-refresh, for the same reason the board has none: this query is expensive enough that a
  // poll from every open tab was on its own enough to saturate the database. The Refresh button (and
  // the "updated HH:mm:ss IST" stamp beside the count) is how fresh rows are pulled; every action
  // reloads on completion, so the page is never stale after something an operator actually did.
  const reloadRef = useRef(load);
  reloadRef.current = load;

  // Open a trip on Loginext. Loginext's page needs THEIR numeric tripId, which they never send us,
  // so the backend resolves it from the trip name (see /oms/logistics/trip-link/). The tab is opened
  // SYNCHRONOUSLY on the click and pointed at the URL once it resolves — opening it after the await
  // is what browsers treat as a pop-up and block.
  // A trip's planned route — its stops in run order, read from the DELIVERYPLANNING events OMS
  // already stores. No carrier call, so this is cheap enough to open freely.
  const openTripMap = async (tripName: string) => {
    setLoadingRoute(tripName);
    const route = await getPlannedTrip(tripName);
    setLoadingRoute(null);
    if (route) setTripRoute(route); // a failure was already reported by the api layer
  };

  // One booking's LogiNext tracking page, EMBEDDED. This is the surface their docs support framing.
  // Built in the browser when the iFrame token is bundled (REACT_APP_LOGINEXT_IFRAME_TOKEN), else
  // minted by the backend from its own copy — see loginext_embed.ts on the trade between the two.
  const trackShipment = async (row: LogisticsOrderRow) => {
    // LogiNext bookings only — see the note on the menu item. Their tracking API answers 200 with an
    // empty body for a reference it does not own, which their page shows as a generic error.
    if (!row.provider_ref || row.provider_type !== "own_tms") return;
    setTrackSource("ours");
    const url =
      trackingEmbedUrl({
        orderNo: row.provider_ref,
        // THEIR reference (`awb_no`), not ours, whenever we hold it — `provider_ref` is our order
        // number, and their middlemile tracking endpoint is keyed on the referenceId LogiNext
        // minted. Asking by `ordno` resolves nothing, which their page renders as the empty
        // "no shipment" illustration rather than an error. This is the same rule the backend's
        // live-ETA fan-out states outright (`live_eta._eligible`: awb_no, "NOT provider_ref, which
        // is our order number and returns the wrong row"), and `provider_ref` stays as the fallback
        // for the handful of bookings with no awb_no yet.
        orderReferenceId: row.awb_no,
        // shipmentOrderTypeCd is deliberately NOT sent. It is optional in their docs, it was not in
        // the URL shape verified against their API, and a wrong value is a plausible way to make
        // their page fail — this board carries both miles, so we would often guess it wrong.
      }) ?? (await getLoginextTrackingEmbed(row.provider_ref));
    // Opened whether or not LogiNext gave us a URL — our own view needs none, and refusing to open
    // was the old behaviour that left an operator with nothing when their side would not answer.
    setFrame({
      // The PO is what an operator recognises; the ordno is what LogiNext was actually asked for,
      // and naming it is what makes a failure diagnosable rather than mysterious.
      title: `Tracking · ${row.po_numbers?.[0] || row.provider_ref} · ${row.provider_ref}`,
      url: url ?? null,
      docId: row.id,
    });
  };

  const onSaved = (updated: LogisticsOrderRow) =>
    // Patch in place on the page in hand — there is no full list to scan any more.
    setTrips((prev) =>
      prev.map((t) => ({ ...t, rows: t.rows.map((r) => (r.id === updated.id ? updated : r)) }))
    );

  const filtersApplied = !!(
    city ||
    warehouse ||
    attention ||
    search ||
    expressOnly ||
    vendor ||
    driver ||
    deliveryState ||
    mile
  );

  // The CSV, over the SAME scope the board is showing — including the search-widens-the-dates rule
  // below, so a file downloaded mid-search contains what the screen contains.
  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadCarrierTripsCsv({
        ...scope,
        ...(search ? { dispatch_from: undefined, dispatch_to: undefined } : {}),
        search: search || undefined,
      });
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setCity(undefined);
    setWarehouse(undefined);
    setAttention("");
    setExpressOnly(false);
    setVendor(undefined);
    setDriver(undefined);
    setDeliveryState(undefined);
    setMile(undefined);
    setSearchInput("");
    setSearch("");
  };

  const columns = useMemo(
    () =>
      buildVendorColumns({
        onSaved,
        reload: () => reloadRef.current({ silent: true }),
        onView: setViewing,
        onViewNotes: setNotesFor,
        onTrack: trackShipment,
        liveEta,
        liveEtaLoading,
        liveEtaFailed,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveEta, liveEtaLoading, liveEtaFailed]
  );

  // Picker options and the filtered/grouped trips now arrive from the server — see `load`. They
  // had to: each was derived from the full row set, so a page of 20 trips would have emptied the
  // dropdowns and made every count describe only what happened to be on screen.
  const vendorOptions = stats?.vendors ?? [];
  const driverOptions = stats?.drivers_available ?? [];
  const drivers = stats?.drivers ?? [];

  // Does the scope cover more than one morning? Driver punctuality is read as a clock time over a
  // single day and as a tally of days over a range — see `driver_performance.ts`. A SEARCH counts as
  // a range whatever the pickers say: it deliberately ignores the date bounds (the server drops
  // them, so the strip above says "searching any date or status"), and printing one arrival time
  // for a hit from three weeks ago would be reading a figure the filter no longer describes.
  const multiDay = !!search || !dateRange[0].isSame(dateRange[1], "day");

  // --- Live ETA -------------------------------------------------------------------------------
  // Fetched for the trips the operator has OPENED, not for the whole board.
  //
  // The board-wide version of this was wrong, and wrong in the worst way — quietly. LogiNext's
  // shipment endpoint takes one reference at a time, so a board-wide pass meant hundreds of calls
  // and had to be capped; past the cap, rows silently fell back to the planned ETA with nothing to
  // say they had never been asked. A three-day filter holds ~437 eligible shipments against a cap
  // of 360, so it started lying as soon as the window grew past a day.
  //
  // Panels open collapsed and an operator reads one trip at a time, so following the open panels
  // asks about the six rows actually on screen instead of seven hundred that are not. No cap is
  // needed, nothing is silently skipped, and the answer arrives immediately rather than after a
  // board-long sweep. The server still caches each shipment briefly, so reopening a panel — or a
  // second operator opening the same trip — costs nothing.
  const ETA_BATCH = 120; // matches the server's own per-request cap; a panel is far smaller

  // The eligible bookings inside the open panels, as a STABLE key: every load hands back fresh row
  // OBJECTS, so keying on them would re-ask on each silent reload even for the same shipments.
  const openEtaKey = useMemo(() => {
    const open = new Set(openTrips);
    return trips
      .filter((t) => open.has(t.key))
      .flatMap((t) => t.rows)
      .filter(canHaveLiveEta)
      .map((r) => r.id)
      .sort((a, b) => a - b)
      .join(",");
  }, [trips, openTrips]);

  // Bookings already asked about for the CURRENT set of rows. A ref, not state, so consulting it
  // cannot itself retrigger the effect that fills it.
  const etaAskedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    // The column only exists on the Trips tab — no reason to call a third party for a table that
    // is not on screen.
    if (tab !== "trips" || !openEtaKey) return;
    // Only what we have NOT already asked about. Opening a second trip while the first is still
    // open must ask about the second one's stops alone — without this, every newly opened panel
    // re-sends every id already on screen.
    const ids = openEtaKey.split(",").map(Number).filter((id) => !etaAskedRef.current.has(id));
    if (!ids.length) return;
    let cancelled = false;
    (async () => {
      setLiveEtaLoading(true);
      setLiveEtaFailed(false);
      try {
        for (let i = 0; i < ids.length; i += ETA_BATCH) {
          const batch = ids.slice(i, i + ETA_BATCH);
          const { results, failed } = await getLiveEtas(batch);
          // A reload that landed while this was in flight owns the state now — drop this result.
          if (cancelled) return;
          if (failed) {
            setLiveEtaFailed(true);
            // NOT marked as asked: a failed batch must be retried when the panel is next opened,
            // or one blip would leave those rows on the planned ETA until the next reload.
            continue;
          }
          for (const id of batch) etaAskedRef.current.add(id);
          // MERGED into what is already known, never replacing it: opening a second trip must not
          // blank the first one's ETAs.
          setLiveEta((prev) => {
            const next = { ...prev };
            for (const [id, eta] of Object.entries(results)) next[Number(id)] = eta;
            return next;
          });
        }
      } finally {
        // `finally`, so an early return on cancel cannot strand the column reading "checking…".
        setLiveEtaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openEtaKey, tab, etaGen]);

  // A fresh load means fresh trucks — drop ETAs fetched against the previous set so a reopened
  // panel re-asks rather than showing a figure from before the refresh. Cheap: the server's cache
  // answers a re-ask without touching LogiNext.
  useEffect(() => {
    setLiveEta({});
    etaAskedRef.current = new Set();
    setEtaGen((n) => n + 1);
  }, [trips]);


  const rowClassName = (r: LogisticsOrderRow) =>
    r.sla_state === "breached" ? styles.boardRowBreached ?? "" : "";

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <CarOutlined className={styles.headerIcon} />
        <h2 className={styles.title}>Carrier Dashboard</h2>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Could not load the latest trips"
          description="What you see below may be out of date. Check your connection and try again."
          action={
            <Button size="small" danger onClick={() => load()}>
              Try again
            </Button>
          }
        />
      )}

      <VendorControls
        summary={stats ? { total: stats.total } : undefined}
        attention={attention}
        setAttention={setAttention}
        dateRange={dateRange}
        setDateRange={setDateRange}
        city={city}
        setCity={setCity}
        warehouse={warehouse}
        setWarehouse={setWarehouse}
        warehouses={warehouses}
        expressOnly={expressOnly}
        setExpressOnly={setExpressOnly}
        vendor={vendor}
        setVendor={setVendor}
        vendorOptions={vendorOptions}
        driver={driver}
        setDriver={setDriver}
        driverOptions={driverOptions}
        mile={mile}
        setMile={setMile}
        deliveryState={deliveryState}
        setDeliveryState={setDeliveryState}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        filtersApplied={filtersApplied}
        clearFilters={clearFilters}
        onExport={exportCsv}
        exporting={exporting}
        onRefresh={() => load()}
        loading={loading}
      />

      {/* Two tabs over ONE server-filtered set: the trips themselves, and the summary
          that counts them. The filter bar sits ABOVE the tabs on purpose — it scopes whichever tab
          is open, so a figure on Summary always describes the list on Trips. */}
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: "trips",
            label: "Trips",
            children: (
              <>
        <div style={{ marginBottom: 4, color: "#8c8c8c", fontSize: 13 }}>
          {bookingCount === 0
            ? search
              ? "Nothing matches this search"
              : "No booking is on a trip for this date / filter"
            : `${bookingCount} booking${bookingCount === 1 ? "" : "s"} across ${tripCount} trip${
                tripCount === 1 ? "" : "s"
              }`}
          {search && <span style={{ marginLeft: 8 }}>· searching any date or status</span>}
          {updatedAt && (
            <span style={{ marginLeft: 8 }}>· updated {fmtIST(updatedAt, "HH:mm:ss")} IST</span>
          )}
        </div>

        {/* Said on screen, not just in the code: this page is not yet one transporter's own work. */}
        <div style={{ marginBottom: 14, color: "#ad6800", fontSize: 12 }}>
          Phase 1 — shows only bookings on a Loginext trip, and every such trip in this scope rather
          than one transporter's.
        </div>

        {trips.length === 0 ? (
          <Empty
            description={
              loading
                ? "Loading…"
                : search
                ? "No booking on a trip matches this search."
                : "No booking is on a trip for this date / filter."
            }
          />
        ) : (
          // One FOLDED panel per trip — the shape Delivery V2's "Delivery Assigned" tab uses. All
          // collapsed on open: the header carries enough (trip, vendor, vehicle, driver, stops) to
          // find the run you want without expanding any of them.
          // `onChange` drives the live-ETA pass: opening a trip is what asks LogiNext about its
          // stops, so nothing is fetched for trips nobody has looked at.
          <Collapse
            style={{ marginBottom: 16 }}
            onChange={(keys) => setOpenTrips(Array.isArray(keys) ? keys : [keys])}
          >
            {trips.map((trip) => {
              // The header values come off the TRIP, not off a row: the server resolves them once
              // per trip, so a page boundary can never change what the heading says.
              const names = trip.trip_name ? [trip.trip_name] : [];
              return (
                <Collapse.Panel
                  key={trip.key}
                  header={
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", width: "100%" }}>
                      <HeaderField label="Trip" grow={2}>
                        {names.length ? (
                          names.map((name) => (
                            <span key={name} style={{ whiteSpace: "nowrap" }}>
                              <Tag color="blue" style={{ marginInlineEnd: 2 }}>
                                {name}
                              </Tag>
                              {/* This trip's planned route. stopPropagation, so it doesn't also
                                  fold the panel it sits in. */}
                              <Tooltip title="See this trip's route on a map">
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EnvironmentOutlined />}
                                  loading={loadingRoute === name}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openTripMap(name);
                                  }}
                                  style={{ padding: 0, height: "auto", marginRight: 10 }}
                                />
                              </Tooltip>
                            </span>
                          ))
                        ) : (
                          <span style={{ color: "#bbb" }}>—</span>
                        )}
                      </HeaderField>
                      <HeaderField label="Vendor">
                        {trip.vendor ?? <span style={{ color: "#bbb" }}>Unknown</span>}
                      </HeaderField>
                      <HeaderField label="Vehicle">
                        {trip.vehicle_number || <span style={{ color: "#bbb" }}>Not set</span>}
                      </HeaderField>
                      <HeaderField label="Driver" grow={2}>
                        {/* The trip's stamped driver (the vendor above is its own payload key), falling
                            back to the booking's own column when the payload carries none. */}
                        {trip.driver_name || <span style={{ color: "#bbb" }}>Not set</span>}
                        {trip.driver_number ? (
                          <span style={{ color: "#888" }}> · {trip.driver_number}</span>
                        ) : null}
                      </HeaderField>
                      <HeaderField label="Stops">{trip.stops}</HeaderField>
                    </div>
                  }
                >
                  <Table
                    rowKey="id"
                    size="small"
                    tableLayout="fixed"
                    columns={columns}
                    dataSource={trip.rows}
                    pagination={false}
                    rowClassName={rowClassName}
                    scroll={{ x: 1480 }}
                    onRow={(r) => ({
                      onClick: () => setItemsFor(r),
                      style: { cursor: "pointer" },
                    })}
                  />
                </Collapse.Panel>
              );
            })}
          </Collapse>
        )}

        {/* One page of trips. `count` is every trip in scope, not this page, so the pager knows how
            far it goes without the page having to hold them all. */}
        {numPages > 1 && (
          <Pagination
            style={{ marginTop: 16, textAlign: "right" }}
            current={page}
            total={tripCount}
            pageSize={PAGE_SIZE}
            showSizeChanger={false}
            onChange={setPage}
            showTotal={(total) => `${total} trips`}
          />
        )}
              </>
            ),
          },
          {
            key: "summary",
            label: "Summary",
            children: (
              <VendorSummary
                stats={stats}
                drivers={drivers}
                tripCount={stats?.trip_count ?? 0}
                multiDay={multiDay}
                loading={loading}
                onOpen={setSummaryRequest}
                onOpenDrivers={() => setDriversOpen(true)}
              />
            ),
          },
        ]}
      />

      {/* Loginext's own trip page, embedded. Their CSP allows it (`frame-ancestors *`), so this is
          a real embed rather than a link out — but the frame carries LOGINEXT's session, not ours:
          if the operator is not signed in to Loginext in this browser (or the browser blocks
          third-party cookies, as Safari does by default), the frame shows their login instead of
          the trip. That is why "Open in new tab" stays in the header — same URL, first-party. */}
      <Drawer
        title={frame ? frame.title : ""}
        placement="right"
        // Both tabs fit in this without a near-full-screen panel: ours is a summary table and a
        // short progress list, and LogiNext's embed reflows to whatever it is given. Capped in vw
        // so a laptop screen keeps the page behind it visible; "Open in new tab" is still in the
        // header for anyone who wants their map full width.
        width="min(880px, 94vw)"
        open={!!frame}
        onClose={() => setFrame(null)}
        destroyOnClose
        bodyStyle={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}
        extra={
          <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Segmented
              size="small"
              value={trackSource}
              onChange={(v) => setTrackSource(v as "ours" | "loginext")}
              options={[
                { label: "Material Depot", value: "ours" },
                // Disabled rather than hidden when their side gave us no URL: the operator should
                // see that the option exists and could not be built, not wonder where it went.
                { label: "LogiNext", value: "loginext", disabled: !frame?.url },
              ]}
            />
            {trackSource === "loginext" && frame?.url && (
              <Tooltip title="Open this tracking view in its own tab">
                <Button
                  size="small"
                  icon={<ExportOutlined />}
                  href={frame.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in new tab
                </Button>
              </Tooltip>
            )}
          </span>
        }
      >
        {frame && trackSource === "ours" && (
          <div style={{ padding: 20, overflow: "auto" }}>
            <TrackingPanel docId={frame.docId} />
          </div>
        )}
        {frame && trackSource === "loginext" && frame.url && (
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            {/* Said up front, because a login screen inside the frame otherwise looks broken. We
                cannot detect it — the frame is cross-origin and its contents are unreadable to us —
                so the two ways out are offered unconditionally rather than on a condition we can
                test. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                padding: "8px 16px",
                background: "#fffbe6",
                borderBottom: "1px solid #ffe58f",
                fontSize: 12,
                color: "#614700",
              }}
            >
              <span>
                LogiNext's tracking view, embedded. Live position updates on their side — reload if
                it looks stale.
              </span>
              <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <Button
                  size="small"
                  icon={<ExportOutlined />}
                  href={frame.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in new tab
                </Button>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => setFrameNonce((n) => n + 1)}
                >
                  Reload
                </Button>
              </span>
            </div>
            <iframe
              key={`${frame.url}#${frameNonce}`}
              title={`LogiNext ${frame.title}`}
              src={frame.url}
              style={{ flex: 1, width: "100%", minHeight: 0, border: "none", display: "block" }}
            />
          </div>
        )}
      </Drawer>

      {/* A trip's planned route: stops numbered in run order, joined by a straight line. */}
      <Drawer
        title={
          tripRoute
            ? `${tripRoute.trip_name}${tripRoute.driver ? ` · ${tripRoute.driver}` : ""} — ${
                tripRoute.stops.length
              } stop${tripRoute.stops.length === 1 ? "" : "s"}`
            : ""
        }
        placement="right"
        width="88%"
        open={!!tripRoute}
        onClose={() => setTripRoute(null)}
        destroyOnClose
        // The map fills the drawer: its sidebar scrolls on its own, the page never does.
        bodyStyle={{ padding: 0, overflow: "hidden", display: "flex" }}
      >
        {tripRoute && (
          <TripMap stops={tripRoute.stops} liveLocation={tripRoute.live_location} trip={tripRoute} />
        )}
      </Drawer>

      {/* The list behind a clicked Summary figure — sliced from the same rows, no fetch. */}
      <SummaryListDrawer
        request={summaryRequest}
        scope={{ ...scope, search: search || undefined }}
        onClose={() => setSummaryRequest(null)}
      />

      {/* The drivers behind the performance figures — same rows, one row per driver. */}
      <DriverPerformanceDrawer
        open={driversOpen}
        drivers={drivers}
        multiDay={multiDay}
        onClose={() => setDriversOpen(false)}
      />

      {/* What is on the truck — the same contents modal the board opens on a row click. */}
      <AllocationItemsModal
        booking={itemsFor}
        hideShipSeparately
        onClose={() => setItemsFor(null)}
        onChanged={() => load({ silent: true })}
      />

      {/* Read-only: everything we hold about one booking. The board's drawer offers Edit / Move
          carrier from here too; those are ops-desk mutations, so this page shows the details only.
          `hideOrderRow` drops the link out to the order's page — Lead and Client stay; the
          Tracking row goes too, since carrier tracking is reached from the row menu here. */}
      <Drawer
        title={viewing ? `Booking #${viewing.id}` : "Booking"}
        placement="right"
        width={520}
        open={!!viewing}
        onClose={() => setViewing(null)}
        destroyOnClose
      >
        {viewing && <BookingDetails view={fromRow(viewing)} hideOrderRow hideTrackingRow />}
      </Drawer>

      {notesFor?.sales_order_id && (
        <CrmIssueDrawer
          salesOrderId={notesFor.sales_order_id}
          open={!!notesFor}
          onClose={() => setNotesFor(null)}
        />
      )}

    </div>
  );
};

export default VendorDashboard;
